export const BLACKBOARD_SCRIPTURE_INSERT_EVENT =
  'shepherds:blackboard-scripture-insert';

const pendingInsertions = new Map();
const completedInsertions = new Map();
const MAX_COMPLETED_INSERTIONS = 200;
const NO_SUBSCRIBER_TIMEOUT_MS = 8000;

const getDefaultTarget = () =>
  typeof window === 'undefined' ? null : window;

const assertPayload = (detail) => {
  if (!detail || typeof detail !== 'object') {
    throw new Error('A Blackboard Scripture payload is required.');
  }
  if (!detail.id || !detail.reference || !detail.text) {
    throw new Error('Blackboard Scripture payload is missing required content.');
  }
  if (!['text', 'sticky'].includes(detail.mode)) {
    throw new Error('Blackboard Scripture mode must be text or sticky.');
  }
  if (!['verse', 'chapter'].includes(detail.scope)) {
    throw new Error('Blackboard Scripture scope must be verse or chapter.');
  }
  if (
    !Array.isArray(detail.translations) ||
    detail.translations.length < 1 ||
    detail.translations.length > 2
  ) {
    throw new Error(
      'Blackboard Scripture payload requires one standalone or two aligned translations.'
    );
  }
  return detail;
};

const rememberCompletion = (id, result) => {
  completedInsertions.set(id, result);
  while (completedInsertions.size > MAX_COMPLETED_INSERTIONS) {
    completedInsertions.delete(completedInsertions.keys().next().value);
  }
};

const createPendingInsertion = (payload, target) => {
  let resolveCompletion;
  let rejectCompletion;
  const completion = new Promise((resolve, reject) => {
    resolveCompletion = resolve;
    rejectCompletion = reject;
  });
  return {
    payload,
    target,
    claimedBy: null,
    completion,
    resolveCompletion,
    rejectCompletion,
    subscriberTimer: null,
  };
};

const settlePendingInsertion = (entry, result, error = null) => {
  if (pendingInsertions.get(entry.payload.id) !== entry) return;
  pendingInsertions.delete(entry.payload.id);
  if (entry.subscriberTimer) clearTimeout(entry.subscriberTimer);
  if (error) {
    entry.rejectCompletion(error);
    return;
  }
  const outcome = result || { success: true, payload: entry.payload };
  rememberCompletion(entry.payload.id, outcome);
  entry.resolveCompletion(outcome);
};

const emitInsertion = (entry) => {
  if (
    !entry ||
    entry.claimedBy ||
    !entry.target?.dispatchEvent ||
    typeof CustomEvent === 'undefined'
  ) return;
  entry.target.dispatchEvent(
    new CustomEvent(BLACKBOARD_SCRIPTURE_INSERT_EVENT, {
      detail: entry.payload,
    })
  );
};

/**
 * Queues a BiblePanel insertion until a ClassBlackboard subscriber can claim it.
 * The returned Promise settles only after that board confirms insertion or
 * rejects it, so callers never need to display a fire-and-forget success state.
 * Re-dispatching the same payload id returns the same completion and cannot
 * create a second board element.
 */
export const dispatchBlackboardScriptureInsert = (
  detail,
  target = getDefaultTarget()
) => {
  const payload = assertPayload(detail);
  if (!target?.dispatchEvent || typeof CustomEvent === 'undefined') {
    throw new Error('Blackboard insertion is unavailable in this environment.');
  }
  if (completedInsertions.has(payload.id)) {
    return Promise.resolve(completedInsertions.get(payload.id));
  }
  const existing = pendingInsertions.get(payload.id);
  if (existing) return existing.completion;

  const entry = createPendingInsertion(payload, target);
  pendingInsertions.set(payload.id, entry);
  entry.subscriberTimer = setTimeout(() => {
    if (entry.claimedBy || pendingInsertions.get(payload.id) !== entry) return;
    settlePendingInsertion(
      entry,
      null,
      new Error('Blackboard insertion is available only after a class Blackboard is ready.')
    );
  }, NO_SUBSCRIBER_TIMEOUT_MS);
  emitInsertion(entry);
  return entry.completion;
};

/**
 * Subscription helper for ClassBlackboard. The returned function removes the
 * exact listener, which keeps StrictMode mounts and unmounts leak-free.
 */
export const subscribeBlackboardScriptureInsert = (
  listener,
  target = getDefaultTarget()
) => {
  if (typeof listener !== 'function') {
    throw new Error('A Blackboard Scripture listener is required.');
  }
  if (!target?.addEventListener) return () => {};

  const subscriberToken = {};
  const handleInsert = (event) => {
    const payload = assertPayload(event.detail);
    const entry = pendingInsertions.get(payload.id);
    if (entry && entry.target !== target) return;
    if (entry?.claimedBy && entry.claimedBy !== subscriberToken) return;
    if (entry) {
      entry.claimedBy = subscriberToken;
      if (entry.subscriberTimer) {
        clearTimeout(entry.subscriberTimer);
        entry.subscriberTimer = null;
      }
    }

    let result;
    try {
      result = listener(payload, event);
    } catch (error) {
      if (entry) settlePendingInsertion(entry, null, error);
      return;
    }
    if (!entry) return;
    Promise.resolve(result).then(
      (outcome) => settlePendingInsertion(entry, outcome),
      (error) => settlePendingInsertion(entry, null, error)
    );
  };
  target.addEventListener(BLACKBOARD_SCRIPTURE_INSERT_EVENT, handleInsert);

  // A Bible action can happen before the board is mounted. Drain on the next
  // microtask so React finishes the subscribing commit before insertion starts.
  Promise.resolve().then(() => {
    pendingInsertions.forEach((entry) => {
      if (entry.target === target) emitInsertion(entry);
    });
  });

  return () => {
    target.removeEventListener(BLACKBOARD_SCRIPTURE_INSERT_EVENT, handleInsert);
  };
};
